import { useEffect, useRef } from "react";

/**
 * Reusable Production-Ready Modal Container using Tailwind CSS.
 *
 * Fixed Overlap Issue:
 *   - Uses z-[9999] overlay so the modal sits above the AppShell sticky header (z-150).
 *   - Centered dialog with backdrop-blur and keyboard/click-outside navigation.
 */
export function ModalShell({
  title,
  subtitle,
  onClose,
  children,
  maxWidth = "max-w-xl",
  zIndex = "z-[9999]",
  height = "max-h-[90vh]",
  fixedHeight = false,
  className = "",
}) {
  const modalRef = useRef(null);

  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const heightClass = fixedHeight
    ? "h-[85vh] min-h-[560px] max-h-[90vh]"
    : height;

  return (
    <div
      className={`fixed inset-0 ${zIndex} flex items-center justify-center p-3 sm:p-6 bg-slate-900/60 backdrop-blur-sm overflow-y-auto`}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose?.();
        }
      }}
    >
      <div className={`w-full ${maxWidth} my-auto relative`}>
        <div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          className={`w-full bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden ${heightClass} ${className}`}
        >
          {/* Modal Header */}
          <div className="px-4 py-4 sm:px-6 sm:py-5 border-b border-slate-200 flex items-start justify-between bg-white shrink-0">
            <div>
              <h2
                id="modal-title"
                className="text-lg sm:text-xl font-bold text-[#16294C] leading-tight"
              >
                {title}
              </h2>
              {subtitle && (
                <p className="text-xs sm:text-sm text-slate-500 mt-1 font-normal">
                  {subtitle}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close modal"
              className="w-10 h-10 -mr-2 -mt-1 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors focus:outline-none focus:ring-2 focus:ring-[#2563eb]/30 cursor-pointer shrink-0"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Modal Body & Footer */}
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * Reusable Form Input Field formatted for Tailwind CSS Modals.
 */
export function ModalInputField({
  label,
  placeholder,
  type = "text",
  value,
  onChange,
  required = false,
  disabled = false,
  error,
  hint,
  as = "input",
  options,
  list,
  rows = 3,
  className = "",
  ...rest
}) {
  return (
    <div className={`flex flex-col ${className}`}>
      {label && (
        <label className="text-xs font-semibold text-slate-700 block mb-1.5">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      {as === "select" ? (
        <select
          value={value ?? ""}
          onChange={onChange}
          disabled={disabled}
          required={required}
          className={`w-full rounded-lg border ${
            error
              ? "border-red-500 ring-2 ring-red-500/20"
              : "border-slate-300 focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20"
          } px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none transition-all bg-white disabled:bg-slate-100 disabled:text-slate-400 cursor-pointer min-h-[44px]`}
          {...rest}
        >
          <option value="" disabled>
            {placeholder || "Select..."}
          </option>
          {options?.map((o) =>
            typeof o === "string" ? (
              <option key={o} value={o}>
                {o}
              </option>
            ) : (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ),
          )}
        </select>
      ) : as === "textarea" ? (
        <textarea
          value={value ?? ""}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          rows={rows}
          className={`w-full rounded-lg border ${
            error
              ? "border-red-500 ring-2 ring-red-500/20"
              : "border-slate-300 focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20"
          } px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none transition-all bg-white disabled:bg-slate-100 disabled:text-slate-400 resize-y min-h-[80px]`}
          {...rest}
        />
      ) : (
        <input
          type={type}
          value={value ?? ""}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          list={list}
          className={`w-full rounded-lg border ${
            error
              ? "border-red-500 ring-2 ring-red-500/20"
              : "border-slate-300 focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20"
          } px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none transition-all bg-white disabled:bg-slate-100 disabled:text-slate-400 min-h-[44px]`}
          {...rest}
        />
      )}
      {error && (
        <span className="text-xs font-medium text-red-600 mt-1">{error}</span>
      )}
      {hint && !error && (
        <span className="text-xs text-slate-500 mt-1">{hint}</span>
      )}
    </div>
  );
}
